import {
  Badge,
  Box,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  PlatformRequirementDocumentVerdict,
  PlatformRequirementIssueStatus,
} from '@client/graphql/__generated__/types';
import { ExternalLinkIcon } from 'lucide-react';
import { FC, MouseEvent } from 'react';
import { match, P } from 'ts-pattern';
import { formatAttemptTimestamp as formatChecklistTimestamp } from './autopilotAttempt.format';
import { formatPlatformRequirementIssueStatus } from './platformRequirementIssueStatus.format';

/**
 * Whether platform-requirement evaluation has produced data for the selected
 * checklist item. Drives the difference between an honest "no data" state (no
 * evidence evaluation ran) and a positive clean state.
 *
 * - `not_evaluated`: no evaluation evidence — the persisted view shows this
 *   whenever there are no current problems, since it cannot prove clean.
 * - `in_progress`: a live re-evaluation is running (internal live view only).
 * - `evaluated`: evaluation completed; a "No mismatch" clean state is truthful.
 * - `unavailable`: evaluation failed or errored.
 */
export type ChecklistEvaluationStatus =
  | 'not_evaluated'
  | 'in_progress'
  | 'evaluated'
  | 'unavailable';

/** A contract document identified within an attached document. */
export interface ChecklistContractDocumentView {
  form: string | null;
  documentCategory: string | null;
}

/** A document evaluated for a checklist item, with mismatch + contract-doc context. */
export interface ChecklistEvaluationDocumentView {
  documentName: string;
  isProblemDocument: boolean;
  /** Live verdict, when known (internal live view); absent on the persisted view. */
  verdict?: PlatformRequirementDocumentVerdict | null;
  ingestionDocumentId?: string | null;
  platformDocumentId?: string | null;
  contractDocuments: ChecklistContractDocumentView[];
}

/**
 * A checklist document shown as context when the selected checklist item has no
 * current issue (the "None right now" empty state on the internal drawer).
 */
export interface ChecklistContextDocumentView {
  id: string;
  documentName: string;
  hasCurrentProblem: boolean;
  /** Live verdict, when known (internal live view); absent on the persisted view. */
  verdict?: PlatformRequirementDocumentVerdict | null;
  /** Identified form/category for this attachment (internal live view). */
  contractDocuments?: ChecklistContractDocumentView[];
}

/** One projected checklist mismatch issue. Surfaces map their fragment into this. */
export interface ChecklistIssueView {
  publicIssueId: string;
  status: PlatformRequirementIssueStatus;
  groupKey: string;
  /**
   * Evaluation run that produced this issue. Persisted issues carry the backing
   * problem's run id; the live preview uses the sentinel `'live'`. Rows from
   * different runs must never be merged into one group.
   */
  evaluationRunId: string;
  /** When the recorded evaluation ran. Null for the live preview. */
  evaluatedAt: unknown;
  isAgentActionable: boolean;
  checklistItemName: string;
  checklistSectionName?: string | null;
  allowedDocumentCategories: string[];
  allowedForms: string[];
  /** Documents evaluated in this run, from live or persisted evidence. */
  evaluationDocuments: ChecklistEvaluationDocumentView[];
  /**
   * Free-text reason given when this issue was ignored (the hiding review's
   * `message`). Shown on hover on the crossed-out checklist item so the reviewer
   * sees the rationale. Undefined/null for open issues and for ignores made
   * without a reason.
   */
  ignoreReason?: string | null;
}

/**
 * Agent-only actions. Provided by the agent compliance review surface.
 *
 * Both callbacks take the whole group's issues rather than one issue at a time:
 * the affordances are group-scoped, and the caller prompts for a single ignore
 * reason covering the group. Collecting that reason needs a modal, which belongs
 * on the agent surface — this component is also rendered by the internal app, so
 * it must not instantiate one itself.
 */
interface ChecklistIssueActions {
  /**
   * The agent asked to ignore the group's open issues. Reporting the intent
   * rather than performing it is deliberate: the caller prompts for the reason
   * and owns the write, so this component never has to know about either.
   */
  onIgnore: (issues: ChecklistIssueView[]) => void;
  /** Un-ignores every agent-ignored issue in the group. */
  onUndoIgnore: (issues: ChecklistIssueView[]) => void;
  isIgnoring: boolean;
}

type ChecklistIssuesListProps = {
  issues: ChecklistIssueView[];
  selectedIngestionDocumentId?: string;
  selectedPlatformDocumentId?: string | null;
  /**
   * Evaluation state for the selected checklist item. Distinguishes an honest
   * "no data" state from a positive clean state and from a failed evaluation.
   */
  evaluationStatus: ChecklistEvaluationStatus;
  /** Tightens spacing and hides the QA Section label (embedded SkySlope drawer). */
  compact?: boolean;
  /** Selected checklist item name, shown in the no-issue empty state. */
  checklistItemName?: string | null;
  /**
   * The selected checklist item's allowed categories/forms, shown as the
   * "Needs at least one attachment classified with:" box in the no-issue empty
   * state. Known on the live surface; absent on the persisted no-data surface.
   */
  requiredDocumentCategories?: string[];
  requiredForms?: string[];
  /** Attached docs shown when there is no current issue for the selected item. */
  contextDocuments?: ChecklistContextDocumentView[];
  /**
   * Internal-only: show the "Evaluated <timestamp>" line beneath each checklist
   * item. External surfaces never show it because the persisted history is
   * incomplete and the timestamp is not agent-facing.
   */
  showEvaluatedAt?: boolean;
  /**
   * Labels a fully-handled checklist item with who handled it, matching the chip
   * validation suggestions already carry. Resubmitted agent issues retain the
   * "Agent ignored" attribution. Both reviewer and agent-review surfaces opt in
   * so every crossed-out row explains why it was handled.
   */
  showStatusChip?: boolean;
  onOpenDocument?: (document: ChecklistEvaluationDocumentView) => void;
} & ({ actions?: never } | { actions: ChecklistIssueActions });

interface ChecklistGroup {
  groupKey: string;
  renderGroupKey: string;
  evaluatedAt: unknown;
  issues: ChecklistIssueView[];
  checklistItemName: string;
  checklistSectionName?: string | null;
  allowedDocumentCategories: string[];
  allowedForms: string[];
  evaluationDocuments: ChecklistGroupedEvaluationDocumentView[];
}

interface ChecklistGroupedEvaluationDocumentView
  extends ChecklistEvaluationDocumentView {
  renderKey: string;
}

const documentKey = (
  document: ChecklistEvaluationDocumentView,
  fallbackKey: string
): string => {
  if (
    document.ingestionDocumentId != null ||
    document.platformDocumentId != null
  ) {
    return `${document.ingestionDocumentId ?? ''}|${
      document.platformDocumentId ?? ''
    }|${document.documentName}`;
  }

  return fallbackKey;
};

const contractDocumentKey = (
  contractDocument: ChecklistContractDocumentView
): string =>
  JSON.stringify([contractDocument.form, contractDocument.documentCategory]);

const mergeContractDocuments = (
  target: ChecklistContractDocumentView[],
  source: ChecklistContractDocumentView[]
): void => {
  const seen = new Set(target.map(contractDocumentKey));
  for (const contractDocument of source) {
    const key = contractDocumentKey(contractDocument);
    if (!seen.has(key)) {
      target.push({ ...contractDocument });
      seen.add(key);
    }
  }
};

/**
 * Groups issues by their external checklist requirement AND evaluation run.
 * Rows from different runs must never be merged even when the checklist activity
 * id is unchanged, so the render key combines `groupKey` with `evaluationRunId`.
 * Within a group, evaluation documents are unioned/de-duplicated (OR-ing mismatch)
 * and the requirement's allowed categories/forms are de-duplicated. Matches the
 * agent surface's semantics so ignore/resubmit stay aligned at the
 * checklist-activity boundary.
 */
const groupIssues = (issues: ChecklistIssueView[]): ChecklistGroup[] => {
  const byKey = new Map<string, ChecklistGroup>();

  for (const issue of issues) {
    const renderGroupKey = JSON.stringify([
      issue.groupKey,
      issue.evaluationRunId,
    ]);
    const group: ChecklistGroup = byKey.get(renderGroupKey) ?? {
      groupKey: issue.groupKey,
      renderGroupKey,
      evaluatedAt: issue.evaluatedAt,
      issues: [],
      checklistItemName: issue.checklistItemName,
      checklistSectionName: issue.checklistSectionName,
      allowedDocumentCategories: [],
      allowedForms: [],
      evaluationDocuments: [],
    };

    group.issues.push(issue);
    group.allowedDocumentCategories = [
      ...new Set([
        ...group.allowedDocumentCategories,
        ...issue.allowedDocumentCategories,
      ]),
    ];
    group.allowedForms = [
      ...new Set([...group.allowedForms, ...issue.allowedForms]),
    ];

    for (const [
      documentIndex,
      document,
    ] of issue.evaluationDocuments.entries()) {
      const key = documentKey(
        document,
        `${issue.publicIssueId}:${documentIndex}:${document.documentName}`
      );
      const existingIndex = group.evaluationDocuments.findIndex(
        (candidate) => candidate.renderKey === key
      );
      if (existingIndex === -1) {
        group.evaluationDocuments.push({
          ...document,
          contractDocuments: [...document.contractDocuments],
          renderKey: key,
        });
      } else {
        const existing = group.evaluationDocuments[existingIndex];
        existing.isProblemDocument =
          existing.isProblemDocument || document.isProblemDocument;
        existing.verdict = match({
          existingVerdict: existing.verdict,
          incomingVerdict: document.verdict,
        })
          .with(
            {
              existingVerdict: PlatformRequirementDocumentVerdict.MISMATCHED,
            },
            () => PlatformRequirementDocumentVerdict.MISMATCHED
          )
          .with(
            {
              incomingVerdict: PlatformRequirementDocumentVerdict.MISMATCHED,
            },
            () => PlatformRequirementDocumentVerdict.MISMATCHED
          )
          .with(
            { existingVerdict: P.nullish },
            ({ incomingVerdict }) => incomingVerdict ?? null
          )
          .otherwise(({ existingVerdict }) => existingVerdict ?? null);
        mergeContractDocuments(
          existing.contractDocuments,
          document.contractDocuments
        );
      }
    }

    byKey.set(renderGroupKey, group);
  }

  return [...byKey.values()];
};

const formatCategory = (value: string): string => value.replaceAll('_', ' ');

type DocumentBadgeTone = 'error' | 'success' | 'neutral';

const DOCUMENT_BADGE_TONE_STYLE: Record<
  DocumentBadgeTone,
  { bg: string; color: string; border?: string }
> = {
  error: {
    bg: 'compliance.notification.error.bg',
    color: 'compliance.notification.error.text',
  },
  success: {
    bg: 'compliance.notification.success.bg',
    color: 'compliance.notification.success.text',
  },
  neutral: {
    bg: 'transparent',
    color: 'compliance.text.muted',
    border: 'compliance.document.border',
  },
};

/**
 * Resolves the status badge for one attached document. "No mismatch" (success)
 * is only shown when there is positive evidence — a live `MATCHED` verdict or a
 * completed `evaluated` state. Without evidence we show a neutral "No data"
 * rather than falsely asserting the attachment is clean (BLO-13160).
 */
const getDocumentStatusBadge = (input: {
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  evaluationStatus: ChecklistEvaluationStatus;
}): { label: string; tone: DocumentBadgeTone } =>
  match(input)
    .with({ verdict: PlatformRequirementDocumentVerdict.MISMATCHED }, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.MATCHED }, () => ({
      label: 'No mismatch',
      tone: 'success' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.UNRESOLVED }, () => ({
      label: 'Unresolved',
      tone: 'neutral' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.UNCONFIGURED }, () => ({
      label: 'No requirement',
      tone: 'neutral' as const,
    }))
    .with({ isProblemDocument: true }, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with({ evaluationStatus: 'evaluated' }, () => ({
      label: 'No mismatch',
      tone: 'success' as const,
    }))
    .otherwise(() => ({ label: 'No data', tone: 'neutral' as const }));

const DocumentStatusBadge: FC<{
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  evaluationStatus: ChecklistEvaluationStatus;
}> = ({ isProblemDocument, verdict, evaluationStatus }) => {
  const { label, tone } = getDocumentStatusBadge({
    isProblemDocument,
    verdict,
    evaluationStatus,
  });
  const style = DOCUMENT_BADGE_TONE_STYLE[tone];

  return (
    <Badge
      bg={style.bg}
      border={style.border ? '1px solid' : undefined}
      borderColor={style.border}
      borderRadius="full"
      color={style.color}
    >
      {label}
    </Badge>
  );
};

interface ChecklistGroupLifecycleSummary {
  displayStatus: PlatformRequirementIssueStatus | null;
}

const CHECKLIST_ISSUE_STATUS_PRIORITY: Record<
  PlatformRequirementIssueStatus,
  number
> = {
  [PlatformRequirementIssueStatus.OPEN]: 3,
  [PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER]: 2,
  [PlatformRequirementIssueStatus.RESUBMITTED]: 1,
  [PlatformRequirementIssueStatus.IGNORED_BY_AGENT]: 0,
};

/**
 * Builds the compact lifecycle summary shown for one checklist group. This is
 * an intentional client-side display policy: any open issue keeps the group
 * open and suppresses the chip; otherwise mixed handled statuses collapse to
 * reviewer ignored, then resubmitted, then agent ignored.
 */
const summarizeGroupLifecycle = (
  issues: ChecklistIssueView[]
): ChecklistGroupLifecycleSummary => {
  const displayStatus = issues.reduce<PlatformRequirementIssueStatus | null>(
    (currentStatus, issue) =>
      currentStatus == null ||
      CHECKLIST_ISSUE_STATUS_PRIORITY[issue.status] >
        CHECKLIST_ISSUE_STATUS_PRIORITY[currentStatus]
        ? issue.status
        : currentStatus,
    null
  );

  return match(displayStatus)
    .with(null, PlatformRequirementIssueStatus.OPEN, () => ({
      displayStatus: null,
    }))
    .with(
      PlatformRequirementIssueStatus.IGNORED_BY_AGENT,
      PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER,
      PlatformRequirementIssueStatus.RESUBMITTED,
      (status) => ({ displayStatus: status })
    )
    .exhaustive();
};

/**
 * Solid "Agent ignored" / "Ignored by reviewer" chip. A resubmitted checklist
 * issue remains labelled "Agent ignored" to match validation errors: the chip
 * describes who handled the issue, while resubmission remains lifecycle state.
 * Kept unstruck and at full contrast (unlike the faded row it sits in) so the
 * viewer can tell at a glance who disposed of the issue — the checklist
 * counterpart to the validation row's chip in {@link BaseValidationItem}.
 */
const formatChecklistIssueStatus = (
  status: PlatformRequirementIssueStatus
): string =>
  match(status)
    .with(
      PlatformRequirementIssueStatus.IGNORED_BY_AGENT,
      PlatformRequirementIssueStatus.RESUBMITTED,
      () => 'Agent ignored'
    )
    .with(
      PlatformRequirementIssueStatus.OPEN,
      PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER,
      formatPlatformRequirementIssueStatus
    )
    .exhaustive();

const ChecklistIssueStatusChip: FC<{
  status: PlatformRequirementIssueStatus;
}> = ({ status }) => (
  <Box
    bg="compliance.tag.bg"
    borderRadius="xs"
    color="compliance.text.secondary"
    flexShrink={0}
    fontSize="xs"
    fontWeight="medium"
    px={1.5}
    py={0.5}
    whiteSpace="nowrap"
  >
    {formatChecklistIssueStatus(status)}
  </Box>
);

const Label: FC<{ children: string; opacity?: number }> = ({
  children,
  opacity,
}) => (
  <Text
    color="compliance.text.muted"
    fontSize="xs"
    fontWeight="medium"
    letterSpacing="wider"
    opacity={opacity}
    textTransform="uppercase"
  >
    {children}
  </Text>
);

/**
 * The "Needs at least one attachment classified with: …" requirement box,
 * listing the checklist item's allowed categories/forms. Shared by the grouped
 * mismatch view and the no-issue context view so the requirement is shown on
 * both the persisted ("last auto-review") and live surfaces. Renders nothing
 * when neither allowed categories nor forms are known.
 */
const RequiredClassificationBox: FC<{
  allowedDocumentCategories: string[];
  allowedForms: string[];
}> = ({ allowedDocumentCategories, allowedForms }) => {
  if (allowedDocumentCategories.length === 0 && allowedForms.length === 0) {
    return null;
  }

  return (
    <Box
      bg="compliance.document.bg"
      border="1px solid"
      borderColor="compliance.document.border"
      borderRadius="md"
      p={3}
    >
      <Text color="compliance.text.secondary" fontSize="sm" mb={2}>
        Needs at least one attachment classified with:
      </Text>
      <Flex align="center" gap={2} wrap="wrap">
        {allowedDocumentCategories.map((category) => (
          <Badge
            key={`category-${category}`}
            bg="compliance.chip.category.bg"
            border="1px solid"
            borderColor="compliance.chip.category.border"
            borderRadius="full"
            color="compliance.chip.category.text"
          >
            {formatCategory(category)}
          </Badge>
        ))}
        {allowedForms.length > 0 && allowedDocumentCategories.length > 0 ? (
          <Text
            as="span"
            color="compliance.text.muted"
            fontSize="xs"
            fontStyle="italic"
          >
            or
          </Text>
        ) : null}
        {allowedForms.map((form) => (
          <Badge
            key={`form-${form}`}
            bg="compliance.chip.form.bg"
            borderRadius="sm"
            color="compliance.chip.form.text"
            fontFamily="mono"
          >
            {form}
          </Badge>
        ))}
      </Flex>
    </Box>
  );
};

/**
 * One attached-document card: name, selected highlight, status badge, and the
 * identified contract documents (form/category). Shared by the grouped mismatch
 * view and the no-issue context view so both the persisted ("last auto-review")
 * and live surfaces render attachments identically. The contract-documents
 * sub-block is shown only when `contractDocuments` is provided (the persisted
 * no-issue surface has no per-document classifications to show).
 */
const AttachedDocumentCard: FC<{
  documentName: string;
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  contractDocuments?: ChecklistContractDocumentView[] | null;
  isSelected: boolean;
  evaluationStatus: ChecklistEvaluationStatus;
  onOpen?: () => void;
}> = ({
  documentName,
  isProblemDocument,
  verdict,
  contractDocuments,
  isSelected,
  evaluationStatus,
  onOpen,
}) => (
  <Box
    bg={isSelected ? 'compliance.selected.bg' : 'transparent'}
    border="1px solid"
    borderColor={
      isSelected ? 'compliance.selected.border' : 'compliance.document.border'
    }
    borderLeftWidth={isSelected ? '3px' : '1px'}
    borderRadius="md"
    p={3}
  >
    <Text color="compliance.text.primary" fontSize="sm" fontWeight="semibold">
      {documentName}
    </Text>

    <Flex align="center" gap={2} mt={1} wrap="wrap">
      {isSelected ? (
        <Badge
          bg="transparent"
          border="1px solid"
          borderColor="compliance.selected.border"
          borderRadius="full"
          color="compliance.selected.text"
        >
          Selected document
        </Badge>
      ) : null}
      <DocumentStatusBadge
        evaluationStatus={evaluationStatus}
        isProblemDocument={isProblemDocument}
        verdict={verdict}
      />
    </Flex>

    {contractDocuments != null ? (
      <Box mt={2}>
        <Label>{`Contract documents (${contractDocuments.length})`}</Label>
        {contractDocuments.length === 0 ? (
          <Text color="compliance.text.muted" fontSize="xs" fontStyle="italic">
            No contract documents identified
          </Text>
        ) : (
          <VStack align="stretch" mt={1} spacing={1}>
            {contractDocuments.map((contractDocument, index) => (
              <Flex key={`cd-${index}`} align="center" gap={2} wrap="wrap">
                {contractDocument.form ? (
                  <Badge
                    bg="compliance.chip.form.bg"
                    borderRadius="sm"
                    color="compliance.chip.form.text"
                    fontFamily="mono"
                  >
                    {contractDocument.form}
                  </Badge>
                ) : (
                  <Text
                    color="compliance.text.muted"
                    fontSize="xs"
                    fontStyle="italic"
                  >
                    no form
                  </Text>
                )}
                {contractDocument.documentCategory ? (
                  <Badge
                    bg="compliance.tag.bg"
                    borderRadius="full"
                    color="compliance.tag.text"
                  >
                    {formatCategory(contractDocument.documentCategory)}
                  </Badge>
                ) : (
                  <Text
                    color="compliance.text.muted"
                    fontSize="xs"
                    fontStyle="italic"
                  >
                    no category
                  </Text>
                )}
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    ) : null}

    {onOpen ? (
      <Flex
        align="center"
        as="button"
        color="compliance.link.fg"
        fontSize="sm"
        fontWeight="semibold"
        gap={1}
        mt={2}
        type="button"
        onClick={onOpen}
      >
        Open
        <Icon as={ExternalLinkIcon} boxSize="12px" />
      </Flex>
    ) : null}
  </Box>
);

/**
 * Read-only checklist mismatch card shared across the internal (light) and
 * agent/external (dark) compliance surfaces. Themed via `compliance.*` tokens.
 * The optional `actions` branch adds the agent-only Ignore/Undo affordances.
 */
export const ChecklistIssuesList: FC<ChecklistIssuesListProps> = (props) => {
  const {
    issues,
    selectedIngestionDocumentId,
    selectedPlatformDocumentId,
    evaluationStatus,
    compact = false,
    checklistItemName,
    requiredDocumentCategories = [],
    requiredForms = [],
    contextDocuments = [],
    showEvaluatedAt = false,
    showStatusChip = false,
    onOpenDocument,
  } = props;
  const actions = 'actions' in props ? props.actions : undefined;

  if (evaluationStatus === 'unavailable') {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        Checklist issues are unavailable.
      </Text>
    );
  }

  if (evaluationStatus === 'in_progress') {
    return (
      <Flex align="center" gap={2}>
        <Spinner size="sm" />
        <Text color="compliance.text.muted" fontSize="sm">
          Evaluating…
        </Text>
      </Flex>
    );
  }

  if (issues.length === 0) {
    if (contextDocuments.length === 0 && checklistItemName == null) {
      return null;
    }

    return (
      <VStack align="stretch" spacing={3}>
        <Text color="compliance.text.muted" fontSize="sm">
          {evaluationStatus === 'evaluated'
            ? 'No mismatches'
            : 'No evaluation data yet'}
        </Text>
        {checklistItemName != null ? (
          <Box>
            <Label>Checklist Item</Label>
            <Text
              color="compliance.text.primary"
              fontSize="sm"
              fontWeight="semibold"
            >
              {checklistItemName}
            </Text>
          </Box>
        ) : null}
        <RequiredClassificationBox
          allowedDocumentCategories={requiredDocumentCategories}
          allowedForms={requiredForms}
        />
        {contextDocuments.length > 0 ? (
          <Box>
            <Label>Documents attached to this item</Label>
            <Text color="compliance.text.muted" fontSize="xs">
              {contextDocuments.length} attached
              {evaluationStatus === 'evaluated'
                ? ` · ${
                    contextDocuments.filter(
                      (document) =>
                        document.hasCurrentProblem ||
                        document.verdict === 'MISMATCHED'
                    ).length
                  } mismatch`
                : ''}
            </Text>
            <VStack align="stretch" mt={1} spacing={2}>
              {contextDocuments.map((document) => {
                const isSelected =
                  document.id === selectedIngestionDocumentId ||
                  document.id === selectedPlatformDocumentId;

                return (
                  <AttachedDocumentCard
                    key={document.id}
                    contractDocuments={document.contractDocuments}
                    documentName={document.documentName}
                    evaluationStatus={evaluationStatus}
                    isProblemDocument={document.hasCurrentProblem}
                    isSelected={isSelected}
                    verdict={document.verdict}
                  />
                );
              })}
            </VStack>
          </Box>
        ) : null}
      </VStack>
    );
  }

  const groups = groupIssues(issues);

  return (
    <VStack align="stretch" spacing={4}>
      {groups.map((group) => {
        const openIssues = group.issues.filter(
          (issue) =>
            issue.isAgentActionable &&
            issue.status === PlatformRequirementIssueStatus.OPEN
        );
        const ignoredIssues = group.issues.filter(
          (issue) =>
            issue.isAgentActionable &&
            issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT
        );
        const { displayStatus } = summarizeGroupLifecycle(group.issues);
        const isResolved = displayStatus != null;
        // Undo remains bound to `ignoredIssues` below: only an agent ignore is
        // reversible from this surface.
        // Applied per-element rather than to the whole group: CSS opacity fades
        // an entire subtree, and a child cannot exceed its parent's value, so a
        // group-level fade would drag the status chip down with it. The chip
        // has to stay legible — it is the only thing saying who handled this.
        const fadedOpacity = isResolved ? 0.6 : 1;
        // One reason normally covers the group (a single Ignore applies the same
        // text to every open issue), but a group can hold issues ignored in
        // separate rounds — so collect the distinct ones rather than the first.
        const ignoreReasons = [
          ...new Set(
            group.issues
              .map((issue) => issue.ignoreReason?.trim())
              .filter((reason): reason is string => Boolean(reason))
          ),
        ];
        const mismatchCount = group.evaluationDocuments.filter(
          (document) => document.isProblemDocument
        ).length;
        const formattedEvaluatedAt = formatChecklistTimestamp(
          group.evaluatedAt
        );

        const handleIgnore = (event: MouseEvent): void => {
          event.stopPropagation();
          if (actions && !actions.isIgnoring) {
            actions.onIgnore(openIssues);
          }
        };
        const handleUndoIgnore = (event: MouseEvent): void => {
          event.stopPropagation();
          if (actions && !actions.isIgnoring) {
            actions.onUndoIgnore(ignoredIssues);
          }
        };

        return (
          <VStack key={group.renderGroupKey} align="stretch" spacing={3}>
            {!compact && group.checklistSectionName ? (
              <Box aria-label="QA section" opacity={fadedOpacity} role="group">
                <Label>QA Section</Label>
                <Text
                  color="compliance.text.primary"
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  {group.checklistSectionName}
                </Text>
              </Box>
            ) : null}

            <Box>
              <Label opacity={fadedOpacity}>Checklist Item</Label>
              <Flex align="center" gap={2}>
                <Tooltip
                  hasArrow
                  bg="compliance.tooltip.bg"
                  // Only meaningful once handled WITH a reason; an open group has
                  // nothing to explain.
                  isDisabled={!isResolved || ignoreReasons.length === 0}
                  label={
                    ignoreReasons.length > 0 ? (
                      <Box>
                        <Heading
                          color="compliance.validation.tooltip.text.primary"
                          mb={1}
                          size="smallUppercase"
                        >
                          Reason for ignoring
                        </Heading>
                        <VStack align="stretch" spacing={1}>
                          {ignoreReasons.map((reason) => (
                            <Text key={reason} fontSize="sm">
                              {reason}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    ) : undefined
                  }
                  variant="muted"
                >
                  <Text
                    color="compliance.text.primary"
                    fontSize="sm"
                    fontWeight="semibold"
                    opacity={fadedOpacity}
                    textDecoration={isResolved ? 'line-through' : undefined}
                  >
                    {group.checklistItemName}
                  </Text>
                </Tooltip>
                {showStatusChip && displayStatus != null ? (
                  <ChecklistIssueStatusChip status={displayStatus} />
                ) : null}
              </Flex>
              {showEvaluatedAt && formattedEvaluatedAt ? (
                <Text
                  color="compliance.text.muted"
                  fontSize="xs"
                  opacity={fadedOpacity}
                >
                  Evaluated {formattedEvaluatedAt}
                </Text>
              ) : null}
            </Box>

            <VStack align="stretch" opacity={fadedOpacity} spacing={3}>
              <RequiredClassificationBox
                allowedDocumentCategories={group.allowedDocumentCategories}
                allowedForms={group.allowedForms}
              />

              <Box>
                <Label>Documents checked</Label>
                <Text color="compliance.text.muted" fontSize="xs">
                  {mismatchCount}{' '}
                  {mismatchCount === 1 ? 'mismatch' : 'mismatches'}
                </Text>
              </Box>

              <VStack align="stretch" spacing={2}>
                {group.evaluationDocuments.map((document) => {
                  const isSelected =
                    (document.ingestionDocumentId != null &&
                      document.ingestionDocumentId ===
                        selectedIngestionDocumentId) ||
                    (document.platformDocumentId != null &&
                      document.platformDocumentId ===
                        selectedPlatformDocumentId);

                  return (
                    <AttachedDocumentCard
                      key={document.renderKey}
                      contractDocuments={document.contractDocuments}
                      documentName={document.documentName}
                      evaluationStatus={evaluationStatus}
                      isProblemDocument={document.isProblemDocument}
                      isSelected={isSelected}
                      verdict={document.verdict}
                      onOpen={
                        onOpenDocument
                          ? () => onOpenDocument(document)
                          : undefined
                      }
                    />
                  );
                })}
              </VStack>

              {actions && actions.isIgnoring ? (
                <Flex
                  align="center"
                  color="compliance.text.muted"
                  fontSize="sm"
                  gap={2}
                >
                  <Spinner size="xs" />
                  Updating
                </Flex>
              ) : null}
              {actions && !actions.isIgnoring && openIssues.length > 0 ? (
                <Text
                  aria-label="Ignore checklist issue"
                  as="button"
                  color="compliance.text.muted"
                  fontSize="sm"
                  textAlign="left"
                  textDecoration="underline"
                  type="button"
                  onClick={handleIgnore}
                >
                  Ignore
                </Text>
              ) : null}
              {actions && !actions.isIgnoring && ignoredIssues.length > 0 ? (
                <Text
                  aria-label="Undo ignore checklist issue"
                  as="button"
                  color="compliance.text.muted"
                  fontSize="sm"
                  textAlign="left"
                  textDecoration="underline"
                  type="button"
                  onClick={handleUndoIgnore}
                >
                  Undo
                </Text>
              ) : null}
            </VStack>
          </VStack>
        );
      })}
    </VStack>
  );
};
